import { oc } from "@orpc/contract";
import { z } from "zod";
import { FEATURE_FLAGS, type FeatureFlagKey } from "@erp/shared-types";

/**
 * Feature flag info schema
 */
export const FeatureFlagInfoSchema = z.object({
  flag: z.string(),
  isActiveInPostHog: z.boolean(),
  enabled: z.boolean(),
  overridden: z.boolean(),
  variantKey: z.string().optional(),
  payload: z.unknown().optional(),
});

/**
 * List feature flags request schema
 */
export const ListFeatureFlagsRequestSchema = z.object({});

/**
 * List feature flags response schema
 */
export const ListFeatureFlagsResponseSchema = z.object({
  flags: z.array(FeatureFlagInfoSchema),
});

/**
 * Set feature flag override request schema
 */
export const SetFeatureFlagOverrideRequestSchema = z.object({
  // A refined string, not z.enum(FEATURE_FLAGS): an enum would put the whole
  // registry in the OpenAPI spec, so every flag removed would be an oasdiff
  // breaking change (request enum value removed). The refine keeps the wire
  // type a plain string, rejects unregistered keys at the boundary (an
  // unregistered override would be stored and never read), and narrows the
  // handler's input to FeatureFlagKey.
  flag: z
    .string()
    .refine((flag): flag is FeatureFlagKey =>
      Object.prototype.hasOwnProperty.call(FEATURE_FLAGS, flag),
    ),
  enabled: z.boolean().optional(),
  variantKey: z.string().optional(),
  payload: z.unknown().optional(),
});

/**
 * Set feature flag override response schema
 */
export const SetFeatureFlagOverrideResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * Remove feature flag override request schema
 */
export const RemoveFeatureFlagOverrideRequestSchema = z.object({
  // Deliberately any string: an override left behind for a flag that has since
  // been removed from the registry must still be removable.
  flag: z.string(),
});

/**
 * Remove feature flag override response schema
 */
export const RemoveFeatureFlagOverrideResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * Clear all feature flag overrides request schema
 */
export const ClearAllFeatureFlagOverridesRequestSchema = z.object({});

/**
 * Clear all feature flag overrides response schema
 */
export const ClearAllFeatureFlagOverridesResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * List feature flags contract
 */
export const listFeatureFlagsContract = oc
  .input(ListFeatureFlagsRequestSchema)
  .output(ListFeatureFlagsResponseSchema)
  .route({
    summary: "List feature flags for the current user",
    description:
      "Admin-only: returns every feature flag evaluated for the current user, with enabled state, variant, payload and whether a local override applies.",
    method: "GET",
  });

/**
 * Set feature flag override contract
 */
export const setFeatureFlagOverrideContract = oc
  .input(SetFeatureFlagOverrideRequestSchema)
  .output(SetFeatureFlagOverrideResponseSchema)
  .route({
    summary: "Override a feature flag for yourself",
    description:
      "Admin-only: sets a per-user override of a registered feature flag's enabled state, variant or payload; refused while imitating another user.",
    method: "POST",
  });

/**
 * Remove feature flag override contract
 */
export const removeFeatureFlagOverrideContract = oc
  .input(RemoveFeatureFlagOverrideRequestSchema)
  .output(RemoveFeatureFlagOverrideResponseSchema)
  .route({
    summary: "Remove a feature flag override",
    description:
      "Admin-only: deletes your override for one feature flag so its normal evaluated value applies again; refused while imitating another user.",
    method: "POST",
  });

/**
 * Clear all feature flag overrides contract
 */
export const clearAllFeatureFlagOverridesContract = oc
  .input(ClearAllFeatureFlagOverridesRequestSchema)
  .output(ClearAllFeatureFlagOverridesResponseSchema)
  .route({
    summary: "Clear all your feature flag overrides",
    description:
      "Admin-only: removes every feature flag override you have set, restoring normal flag evaluation; refused while imitating another user.",
    method: "POST",
  });

/**
 * Feature flags contracts
 */
export const featureFlagsContracts = {
  list: listFeatureFlagsContract,
  set: setFeatureFlagOverrideContract,
  remove: removeFeatureFlagOverrideContract,
  clearAll: clearAllFeatureFlagOverridesContract,
};

// Export types
export type FeatureFlagInfo = z.infer<typeof FeatureFlagInfoSchema>;
export type ListFeatureFlagsRequest = z.infer<
  typeof ListFeatureFlagsRequestSchema
>;
export type ListFeatureFlagsResponse = z.infer<
  typeof ListFeatureFlagsResponseSchema
>;
export type SetFeatureFlagOverrideRequest = z.infer<
  typeof SetFeatureFlagOverrideRequestSchema
>;
export type SetFeatureFlagOverrideResponse = z.infer<
  typeof SetFeatureFlagOverrideResponseSchema
>;
export type RemoveFeatureFlagOverrideRequest = z.infer<
  typeof RemoveFeatureFlagOverrideRequestSchema
>;
export type RemoveFeatureFlagOverrideResponse = z.infer<
  typeof RemoveFeatureFlagOverrideResponseSchema
>;
export type ClearAllFeatureFlagOverridesRequest = z.infer<
  typeof ClearAllFeatureFlagOverridesRequestSchema
>;
export type ClearAllFeatureFlagOverridesResponse = z.infer<
  typeof ClearAllFeatureFlagOverridesResponseSchema
>;
